/**
 * EditorCapitulo - Tela de Escrita Imersiva
 * 
 * "Quando o sistema desaparece, a escrita finalmente aparece."
 * 
 * Uma experiência de escrita onde:
 * - O autor entra em fluxo contínuo
 * - A interface desaparece
 * - A escrita é o centro absoluto
 * - A IA atua como suporte silencioso
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Minimize2, Maximize2, X } from 'lucide-react';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';

// Writing components
import WritingCanvas from '../components/writing/WritingCanvas';
import FloatingToolbar from '../components/writing/FloatingToolbar';
import PostItPanel from '../components/writing/PostItPanel';

// Existing components (preserved)
import Sidebar from '../components/Sidebar';
import GoldenQuestion from '../components/GoldenQuestion';
import PulsoCriativo from '../components/PulsoCriativo';
import { PostItFloating } from '../components/PostItIcon';
import { exportChapterToPDF, exportChapterToDOCX } from '../utils/exportChapter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EditorCapitulo() {
  const { chapterId } = useParams();
  const navigate = useNavigate();
  
  // Core state
  const [chapter, setChapter] = useState(null);
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [wordCount, setWordCount] = useState(0);
  
  // Editor reference
  const editorRef = useRef(null);
  const autoSaveTimer = useRef(null);
  
  // Authorship tracking (silencioso - não afeta UI)
  const previousContentRef = useRef('');
  const authorshipTimer = useRef(null);
  
  // Immersive mode - hides everything except the writing
  const [immersiveMode, setImmersiveMode] = useState(true);
  
  // Post-it panel
  const [showPostIts, setShowPostIts] = useState(false);
  const [notes, setNotes] = useState([]);
  
  // Synopsis post-it (existing feature)
  const [showSynopsis, setShowSynopsis] = useState(false);
  
  // Golden Question modal
  const [showGoldenQuestion, setShowGoldenQuestion] = useState(false);
  
  // Theme (future: could be user preference)
  const [theme] = useState('sepia');
  
  // Font preference (saved per user)
  const [font, setFont] = useState(() => {
    return localStorage.getItem('writing_font_preference') || 'playfair';
  });

  // Font size preference (16-24px, default 18)
  const [fontSize, setFontSize] = useState(() => {
    return parseInt(localStorage.getItem('writing_font_size') || '18', 10);
  });

  // Deep focus mode (zoom + reduced contrast)
  const [deepFocusMode, setDeepFocusMode] = useState(false);
  
  // Chapter navigation
  const [siblingChapters, setSiblingChapters] = useState({ prev: null, next: null });
  const [showNavArrows, setShowNavArrows] = useState(false);
  const navArrowsTimeout = useRef(null);

  // Save font preference
  const handleFontChange = (newFont) => {
    setFont(newFont);
    localStorage.setItem('writing_font_preference', newFont);
  };

  // Font size controls
  const handleFontSizeChange = (delta) => {
    const newSize = Math.min(24, Math.max(16, fontSize + delta));
    setFontSize(newSize);
    localStorage.setItem('writing_font_size', newSize.toString());
  };

  // Toggle deep focus mode
  const toggleDeepFocus = useCallback(() => {
    setDeepFocusMode(prev => {
      const newState = !prev;
      // When entering deep focus, also enable immersive mode (hide sidebar)
      if (newState) {
        setImmersiveMode(true);
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          // Also scroll the writing container if it exists
          const container = document.querySelector('[data-testid="writing-area"]');
          if (container) {
            container.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
      return newState;
    });
  }, []);

  // ═══════════════════════════════════════════════════════════════
  // DATA FETCHING
  // ═══════════════════════════════════════════════════════════════

  // Fetch notes for chapter (declared before fetchChapter)
  const fetchNotes = useCallback(async () => {
    try {
      // Use existing postits endpoint
      const response = await axios.get(
        `${API_URL}/api/chapters/${chapterId}/postits`,
        { withCredentials: true }
      );
      setNotes(response.data || []);
    } catch (error) {
      // Load from localStorage as fallback
      const localNotes = JSON.parse(localStorage.getItem(`notes_${chapterId}`) || '[]');
      setNotes(localNotes);
    }
  }, [chapterId]);

  // Fetch previous and next chapters for navigation
  const fetchSiblingChapters = useCallback(async (bookId, currentOrder) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/books/${bookId}/chapters`,
        { withCredentials: true }
      );
      const chapters = response.data || [];
      const sortedChapters = chapters.sort((a, b) => a.order - b.order);
      const currentIndex = sortedChapters.findIndex(c => c.order === currentOrder);
      
      setSiblingChapters({
        prev: currentIndex > 0 ? sortedChapters[currentIndex - 1] : null,
        next: currentIndex < sortedChapters.length - 1 ? sortedChapters[currentIndex + 1] : null,
      });
    } catch (error) {
      // Silent fail - sibling navigation is optional
    }
  }, []);

  // Fetch chapter data (declared before useEffect)
  const fetchChapter = useCallback(async () => {
    setLoading(true); // Reset loading when fetching new chapter
    setChapter(null); // Clear previous chapter data
    
    try {
      const response = await axios.get(
        `${API_URL}/api/chapters/${chapterId}`,
        { withCredentials: true }
      );
      
      setChapter(response.data);
      setTitle(response.data.title || '');
      
      // Inicializa referência do conteúdo para rastreamento de autoria
      // Extrai texto puro do HTML para comparação
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = DOMPurify.sanitize(response.data.content || '');
      previousContentRef.current = tempDiv.textContent || tempDiv.innerText || '';
      
      // Save last edited chapter for "Voltar a escrever"
      if (response.data.book_id) {
        localStorage.setItem(`lastChapter_${response.data.book_id}`, chapterId);
        // Fetch sibling chapters for navigation
        fetchSiblingChapters(response.data.book_id, response.data.order);
      }
      
      // Fetch notes for this chapter
      fetchNotes();
      
    } catch (error) {
      console.error('Error fetching chapter:', error);
      toast.error('Erro ao carregar capítulo');
    } finally {
      setLoading(false);
    }
  }, [chapterId, fetchSiblingChapters, fetchNotes]);

  // Load chapter on mount
  useEffect(() => {
    fetchChapter();
    return () => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
      }
      if (authorshipTimer.current) {
        clearTimeout(authorshipTimer.current);
      }
    };
  }, [chapterId, fetchChapter]);

  // ═══════════════════════════════════════════════════════════════
  // AUTO-SAVE & MANUAL SAVE
  // ═══════════════════════════════════════════════════════════════

  // Rastreamento silencioso de autoria (Authorship Engine)
  // Registra contribuições de escrita sem afetar a UI
  const trackAuthorship = useCallback(async (currentText) => {
    if (!chapter?.book_id || !chapterId) return;
    
    const previousText = previousContentRef.current;
    
    // Só rastreia se houver mudança significativa
    if (previousText === currentText) return;
    
    try {
      // Envia silenciosamente para o backend
      await axios.post(
        `${API_URL}/api/coautoria/register-writing/${chapter.book_id}`,
        {
          chapter_id: chapterId,
          previous_content: previousText,
          current_content: currentText
        },
        { withCredentials: true }
      );
      
      // Atualiza referência do conteúdo anterior
      previousContentRef.current = currentText;
      
      // Log discreto para debug (removível em produção)
      console.debug('[Authorship] Contribution tracked');
    } catch (error) {
      // Falha silenciosa - não interrompe a experiência de escrita
      console.debug('[Authorship] Track failed (silent):', error?.response?.status);
    }
  }, [chapter?.book_id, chapterId]);

  // Auto-save function (declared before handleContentChange)
  const handleAutoSave = useCallback(async (content) => {
    if (!chapter) return;
    
    try {
      await axios.put(
        `${API_URL}/api/chapters/${chapterId}`,
        { title, content },
        { withCredentials: true }
      );
      setLastSaved(new Date());
    } catch (error) {
      console.error('Auto-save error:', error);
    }
  }, [chapter, chapterId, title]);

  const handleContentChange = useCallback((html, text) => {
    // Update word count
    const words = text.trim().split(/\s+/).filter(w => w.length > 0).length;
    setWordCount(words);
    
    // Trigger debounced auto-save
    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
    }
    
    autoSaveTimer.current = setTimeout(() => {
      handleAutoSave(html);
    }, 2000); // 2 second debounce
    
    // Trigger debounced authorship tracking (5 segundos - mais lento que auto-save)
    if (authorshipTimer.current) {
      clearTimeout(authorshipTimer.current);
    }
    
    authorshipTimer.current = setTimeout(() => {
      trackAuthorship(text);
    }, 5000); // 5 second debounce para não sobrecarregar
    
  }, [chapter, title, trackAuthorship, handleAutoSave]);

  const handleManualSave = async () => {
    if (!editorRef.current) return;
    
    setSaving(true);
    try {
      const content = editorRef.current.getHTML();
      
      await axios.put(
        `${API_URL}/api/chapters/${chapterId}`,
        { title, content },
        { withCredentials: true }
      );
      
      // Try to create a version
      try {
        await axios.post(
          `${API_URL}/api/chapters/${chapterId}/versions`,
          { content, word_count: wordCount },
          { withCredentials: true }
        );
      } catch (e) {
        // Version save is optional - don't fail the main save
      }
      
      setLastSaved(new Date());
      toast.success('Capítulo salvo!', {
        duration: 2000,
        style: { background: '#fef3c7', color: '#92400e' },
      });
      
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // POST-IT NOTES
  // ═══════════════════════════════════════════════════════════════

  const handleAddNote = async (content) => {
    try {
      // Use existing postits endpoint
      const response = await axios.post(
        `${API_URL}/api/chapters/${chapterId}/postits`,
        { content, color: 'yellow' },
        { withCredentials: true }
      );
      setNotes([...notes, response.data]);
      toast.success('Nota adicionada!');
    } catch (error) {
      // Fallback: save locally if API fails
      const newNote = {
        id: Date.now().toString(),
        content,
        created_at: new Date().toISOString(),
      };
      setNotes([...notes, newNote]);
      
      // Save to localStorage as backup
      const localNotes = JSON.parse(localStorage.getItem(`notes_${chapterId}`) || '[]');
      localNotes.push(newNote);
      localStorage.setItem(`notes_${chapterId}`, JSON.stringify(localNotes));
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      // Use existing postits endpoint
      await axios.delete(
        `${API_URL}/api/postits/${noteId}`,
        { withCredentials: true }
      );
      setNotes(notes.filter(n => n.postit_id !== noteId && n.id !== noteId));
    } catch (error) {
      // Fallback: delete locally
      setNotes(notes.filter(n => n.postit_id !== noteId && n.id !== noteId));
      const localNotes = JSON.parse(localStorage.getItem(`notes_${chapterId}`) || '[]');
      localStorage.setItem(
        `notes_${chapterId}`,
        JSON.stringify(localNotes.filter(n => n.id !== noteId))
      );
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // NAVIGATION & KEYBOARD
  // ═══════════════════════════════════════════════════════════════

  const handleBack = () => {
    if (chapter?.book_id) {
      navigate(`/livro/${chapter.book_id}`);
    } else {
      navigate('/meus-livros');
    }
  };

  // ESC to toggle immersive mode, and deep focus mode
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (deepFocusMode) {
          setDeepFocusMode(false);
        } else if (showPostIts) {
          setShowPostIts(false);
        } else if (showSynopsis) {
          setShowSynopsis(false);
        } else {
          setImmersiveMode(!immersiveMode);
        }
      }
      
      // Cmd/Ctrl + S to save
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        handleManualSave();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [immersiveMode, showPostIts, showSynopsis, deepFocusMode]);

  // Show navigation arrows on mouse move near edges
  const handleMouseMoveForNav = useCallback((e) => {
    const threshold = 150;
    const nearLeftEdge = e.clientX < threshold;
    const nearRightEdge = e.clientX > window.innerWidth - threshold;
    
    if ((nearLeftEdge && siblingChapters.prev) || (nearRightEdge && siblingChapters.next)) {
      setShowNavArrows(true);
      if (navArrowsTimeout.current) clearTimeout(navArrowsTimeout.current);
      navArrowsTimeout.current = setTimeout(() => setShowNavArrows(false), 2000);
    }
  }, [siblingChapters]);

  // Navigate to sibling chapter
  const navigateToChapter = (chapterData) => {
    if (chapterData?.chapter_id) {
      navigate(`/editor/${chapterData.chapter_id}`);
    }
  };

  // Double-click handler for deep focus mode
  const handleDoubleClick = useCallback((e) => {
    // Only activate if clicking outside the editor content
    const isEditor = e.target.closest('[data-testid="editor-content"]');
    const isInput = e.target.tagName === 'INPUT';
    if (!isEditor && !isInput) {
      toggleDeepFocus();
    }
  }, [toggleDeepFocus]);

  // ═══════════════════════════════════════════════════════════════
  // EDITOR READY CALLBACK
  // ═══════════════════════════════════════════════════════════════

  const handleEditorReady = useCallback((editor) => {
    editorRef.current = editor;
    
    // Calculate initial word count
    if (editor) {
      const text = editor.getText();
      const words = text.trim().split(/\s+/).filter(w => w.length > 0).length;
      setWordCount(words);
    }
  }, []);

  // ═══════════════════════════════════════════════════════════════
  // LOADING STATE
  // ═══════════════════════════════════════════════════════════════

  if (loading) {
    return (
      <div className="fixed inset-0 bg-[#c9b99a] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 relative">
            <div className="absolute inset-0 rounded-full border-2 border-amber-200/30" />
            <div className="absolute inset-0 rounded-full border-2 border-amber-600 border-t-transparent animate-spin" />
          </div>
          <p className="text-stone-600 font-serif italic">
            Preparando o manuscrito...
          </p>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  return (
    <div 
      className="flex min-h-screen" 
      data-testid="editor-page"
      onMouseMove={handleMouseMoveForNav}
      onDoubleClick={handleDoubleClick}
    >
      
      {/* Sidebar - hidden in immersive mode */}
      {!immersiveMode && <Sidebar />}

      {/* Deep Focus Mode Overlay - contrast reduction for immersion */}
      {deepFocusMode && (
        <div 
          className="fixed inset-0 z-30 pointer-events-none transition-all duration-500"
          style={{
            background: 'radial-gradient(ellipse 50% 40% at 50% 45%, transparent 0%, rgba(20,15,10,0.35) 100%)',
          }}
        />
      )}

      {/* Chapter Navigation Arrows */}
      {siblingChapters.prev && (
        <button
          onClick={() => navigateToChapter(siblingChapters.prev)}
          className={`
            fixed left-4 top-1/2 -translate-y-1/2 z-50
            p-3 rounded-full bg-white/90 shadow-lg
            text-stone-500 hover:text-stone-800 hover:bg-white
            transition-all duration-300
            ${showNavArrows ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4 pointer-events-none'}
          `}
          title={`Capítulo anterior: ${siblingChapters.prev.title || 'Sem título'}`}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
      )}
      
      {siblingChapters.next && (
        <button
          onClick={() => navigateToChapter(siblingChapters.next)}
          className={`
            fixed right-4 top-1/2 -translate-y-1/2 z-50
            p-3 rounded-full bg-white/90 shadow-lg
            text-stone-500 hover:text-stone-800 hover:bg-white
            transition-all duration-300
            ${showNavArrows ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4 pointer-events-none'}
          `}
          title={`Próximo capítulo: ${siblingChapters.next.title || 'Sem título'}`}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      )}

      {/* Main writing area */}
      <main 
        className={`
          flex-1 relative
          ${!immersiveMode ? 'md:ml-64' : ''}
          ${immersiveMode ? 'fixed inset-0 z-40' : ''}
          ${deepFocusMode ? 'scale-[1.7]' : ''}
          transition-transform duration-500
        `}
        style={{
          // Zoom a partir do topo para manter cabeçalho visível
          transformOrigin: deepFocusMode ? 'top center' : 'center',
          // Garantir nitidez das letras no zoom
          ...(deepFocusMode && {
            willChange: 'transform',
            backfaceVisibility: 'hidden',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          })
        }}
      >
        {/* Top bar - minimal, semi-transparent */}
        <div 
          className={`
            absolute top-0 left-0 right-0 z-30
            flex items-center justify-between px-4 py-3
            bg-gradient-to-b from-black/10 to-transparent
            transition-opacity duration-300
            ${immersiveMode ? 'opacity-0 hover:opacity-100' : 'opacity-100'}
          `}
        >
          {/* Left: Back button */}
          <div className="flex items-center gap-2">
            {!immersiveMode ? (
              <button
                onClick={handleBack}
                className="p-2 rounded-lg bg-white/80 hover:bg-white text-stone-600 hover:text-stone-800 transition-colors shadow-sm"
                data-testid="back-button"
              >
                <ArrowLeft size={18} />
              </button>
            ) : (
              <button
                onClick={() => setImmersiveMode(false)}
                className="p-2 rounded-lg bg-white/80 hover:bg-white text-stone-600 hover:text-stone-800 transition-colors shadow-sm flex items-center gap-2"
              >
                <Minimize2 size={16} />
                <span className="text-xs">Sair do modo imersivo</span>
              </button>
            )}
          </div>

          {/* Right: Status & actions */}
          <div className="flex items-center gap-3">
            {/* Pulso Criativo */}
            <PulsoCriativo
              compact={true}
              wordsWritten={wordCount}
              editsCount={0}
              showPhrase={false}
            />

            {/* Synopsis Post-it toggle */}
            {chapter?.synopsis && (
              <button
                onClick={() => setShowSynopsis(!showSynopsis)}
                className="p-2 rounded-lg bg-white/80 hover:bg-white transition-colors shadow-sm"
                title="Ver síntese do capítulo"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <rect x="4" y="6" width="14" height="14" rx="1" fill="#fef08a" stroke="#eab308" strokeWidth="0.5"/>
                  <circle cx="11" cy="5" r="3" fill="#dc2626"/>
                </svg>
              </button>
            )}

            {/* Golden Question */}
            <GoldenQuestion
              bookId={chapter?.book_id}
              chapterId={chapterId}
              compact={true}
              onWriteAnswer={() => {
                setTimeout(() => editorRef.current?.commands.focus('end'), 100);
              }}
            />

            {/* Immersive mode toggle */}
            {!immersiveMode && (
              <button
                onClick={() => setImmersiveMode(true)}
                className="p-2 rounded-lg bg-white/80 hover:bg-white text-stone-600 hover:text-stone-800 transition-colors shadow-sm"
                title="Modo imersivo (ESC para sair)"
              >
                <Maximize2 size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Writing Canvas - The main experience */}
        <WritingCanvas
          content={chapter?.content || ''}
          title={title}
          chapterOrder={chapter?.order}
          onContentChange={handleContentChange}
          onTitleChange={setTitle}
          wordCount={wordCount}
          theme={theme}
          font={font}
          fontSize={fontSize}
          typewriterMode={true}
          onEditorReady={handleEditorReady}
          deepFocusMode={deepFocusMode}
        />

        {/* Floating Toolbar - appears on mouse movement */}
        <FloatingToolbar
          editor={editorRef.current}
          wordCount={wordCount}
          onSave={handleManualSave}
          onPostItClick={() => setShowPostIts(!showPostIts)}
          onMentorClick={() => setShowGoldenQuestion(true)}
          onFontChange={handleFontChange}
          currentFont={font}
          fontSize={fontSize}
          onFontSizeChange={handleFontSizeChange}
          saving={saving}
          lastSaved={lastSaved}
          autoHide={immersiveMode}
          autoHideDelay={3000}
        />

        {/* Post-it Panel */}
        <PostItPanel
          notes={notes}
          onAddNote={handleAddNote}
          onDeleteNote={handleDeleteNote}
          onClose={() => setShowPostIts(false)}
          isOpen={showPostIts}
        />

        {/* Synopsis Post-it Modal */}
        {showSynopsis && (
          <div className="fixed top-20 right-8 z-50 animate-in slide-in-from-top-2 duration-200">
            <PostItFloating
              synopsis={chapter?.synopsis}
              onClose={() => setShowSynopsis(false)}
            />
          </div>
        )}

        {/* Keyboard hints - shown briefly on first load */}
        {immersiveMode && (
          <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-30 opacity-0 hover:opacity-100 transition-opacity">
            <div className="flex items-center gap-4 px-4 py-2 bg-black/60 text-white/70 text-xs rounded-full">
              <span><kbd className="px-1.5 py-0.5 bg-white/20 rounded">ESC</kbd> sair do modo imersivo</span>
              <span><kbd className="px-1.5 py-0.5 bg-white/20 rounded">⌘S</kbd> salvar</span>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
